import { normalizeActivityTypes, routes } from "@ckgo2/shared";
import type { Activity } from "@ckgo2/shared/types";
import type { ActivityFormData } from "./types";

/**
 * Čistá logika editoru inspirace: převod detailu na hodnoty formuláře a stav odkazu na web.
 *
 * Modul schválně neimportuje `i18n`, `api` ani `@/lib/webUrl`: text kategorie starých tipů
 * a adresu webu dostává parametrem, takže ho test (`activityEditor.test.ts`, projekt `admin`)
 * volá bez inicializovaného slovníku a bez prostředí prohlížeče.
 */

/** Výchozí hodnoty nové inspirace. Klíče = klíče `activityToForm` (hlídá test). */
export const ACTIVITY_FORM_DEFAULTS: ActivityFormData = {
  title: "",
  slug: "",
  activityTypes: [],
  difficultyLevel: "",
  duration: "",
  destinationId: "",
  location: "",
  featuredImage: "",
  featuredImageId: null,
  shortDescription: "",
  description: "",
  galleryImages: [],
  galleryImageIds: [],
  included: [],
  tags: [],
  videos: [],
  tips: [],
  testimonialQuote: "",
  testimonialAuthor: "",
  testimonialAuthorImage: "",
  testimonialAuthorImageId: null,
  heroTitle: "",
  heading: "",
  socialReels: [],
  relatedItems: [],
  status: "draft",
  sortOrder: 0,
  isFeatured: false,
  metaTitle: "",
  metaDescription: "",
};

/**
 * Detail inspirace (`GET /api/admin/inspirations/:id`) → hodnoty formuláře, které jsou zároveň
 * tělem uložení. Uložení proto nesmí zahodit data, která editor neukazuje (řez 10, AK14):
 *
 * - Sloupce bez políčka (`priceFrom`, `tipsContent`) ani `countrySlug` (jen pro čtení) převod
 *   nenese; `.partial()` update schéma pak sloupec nepřepíše.
 * - Položky JSON sloupců (`videos`, `socialReels`, `tips`, ploché tipy) jdou dál REFERENCÍ.
 *   Skládání po polích (`{ url, title }`) by tiše zahodilo klíč, který editor nezobrazuje.
 * - Zobrazená pole `included` a `tags` mění `null` na `[]`, protože `StringArrayEditor`
 *   potřebuje pole; žádný čtenář `null` a `[]` nerozlišuje.
 * - Legacy URL (`featuredImage`, `galleryImages`, `testimonialAuthorImage`) zůstávají ve
 *   formuláři bez políčka a vrací se beze změny.
 *
 * `defaultTipTopic` = název jediné kategorie, do které se načtou staré ploché tipy
 * (`{question, answer}[]`): redakce je tak vidí v témže editoru a může je rozdělit; uloží se
 * už jen sekcový tvar. Text dodává volající ze slovníku.
 */
export function activityToForm(a: Activity, defaultTipTopic: string): ActivityFormData {
  return {
    title: a.title || "",
    slug: a.slug || "",
    // Seznam v pořadí výčtu; prázdný seznam = bez typu. Starý `activityType` formulář nenese,
    // plní ho admin API ze seznamu.
    activityTypes: normalizeActivityTypes(a.activityTypes),
    difficultyLevel: a.difficultyLevel || "",
    duration: a.duration || "",
    destinationId: a.destinationId || "",
    location: a.location || "",
    featuredImage: a.featuredImage || "",
    featuredImageId: a.featuredImageId ?? null,
    shortDescription: a.shortDescription || "",
    description: a.description || "",
    galleryImages: a.galleryImages || [],
    galleryImageIds: a.galleryImageIds || [],
    included: a.included || [],
    tags: a.tags || [],
    videos: a.videos || [],
    tips:
      Array.isArray(a.tips) && a.tips.length > 0 && "question" in a.tips[0]
        ? [
            {
              topic: defaultTipTopic,
              items: a.tips as unknown as Array<{ question: string; answer: string }>,
            },
          ]
        : a.tips || [],
    testimonialQuote: a.testimonialQuote || "",
    testimonialAuthor: a.testimonialAuthor || "",
    testimonialAuthorImage: a.testimonialAuthorImage || "",
    testimonialAuthorImageId: a.testimonialAuthorImageId ?? null,
    heroTitle: a.heroTitle || "",
    heading: a.heading || "",
    socialReels: a.socialReels || [],
    relatedItems: a.relatedItems || [],
    status: a.status || "draft",
    sortOrder: a.sortOrder ?? 0,
    isFeatured: a.isFeatured || false,
    metaTitle: a.metaTitle || "",
    metaDescription: a.metaDescription || "",
  };
}

/**
 * Kanonická adresa stránky inspirace z ULOŽENÉHO detailu (ne z rozpracovaného slugu ve
 * formuláři, ten by vedl na 404). `countrySlug` počítá API (`destinationPath.ts`); bez něj
 * (inspirace bez destinace, nebo API bez pole) je adresa plochá a web ji přesměruje 301.
 * Bez detailu nebo slugu `null`.
 */
export function inspirationPageUrl(
  item: Pick<Activity, "slug" | "countrySlug"> | null | undefined,
  webUrl: string,
): string | null {
  if (!item?.slug) return null;
  return webUrl + routes.inspirace({ slug: item.slug, countrySlug: item.countrySlug ?? null });
}

/**
 * Je inspirace na webu? Podle uloženého stavu, ne podle roletky ve formuláři: přepnutá
 * a neuložená roletka by jinak aktivovala odkaz na stránku, která veřejně neexistuje.
 */
export function inspirationPublished(
  item: Pick<Activity, "status"> | null | undefined,
  isNew: boolean,
): boolean {
  return !isNew && item?.status === "published";
}
